import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanInput, StanPhoneInput } from '@/components';
import type { PhoneChange } from '@/components/core-components/StanPhoneInput/StanPhoneInput.types';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { type ProfileFormData, profileSchema } from '@/features/settings/validations/profile';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';
import { useProfile } from '../../queries/useProfile';
import { UsernameChangeModal } from './UsernameChangeModal';
import { useModal } from '@/hooks/useModal';

export const ProfileSection: React.FC = () => {
    const { user } = useUser();
    const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
    const usernameModal = useModal();
    const pendingFormData = useRef<ProfileFormData | null>(null);
    const { mutateAsync: updateProfileMutation, isPending: isSubmitting } = useProfile();

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        watch,
    } = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        defaultValues: {
            full_name: user?.full_name || '',
            username: user?.username || '',
            email: user?.email || '',
            phone_number: user?.phone_number || '',
        },
    });

    // Update form when user data changes
    const resetForm = useEffectEvent((userData: typeof user) => {
        if (userData) {
            reset({
                full_name: userData.full_name || '',
                username: userData.username || '',
                email: userData.email || '',
                phone_number: userData.phone_number || '',
            });
        }
    });

    useEffect(() => {
        resetForm(user);
    }, [user]);

    const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;
    const watchedValues = watch();
    const isFormChanged = isDirty;

    const isButtonDisabled = isSubmitting || !isFormChanged;

    // Check if username has changed from the original
    const hasUsernameChanged = (newUsername: string) => {
        return newUsername !== user?.username;
    };

    const performUpdate = async (data: ProfileFormData) => {
        setFormErrors({});

        try {
            // Call the settings API
            await updateProfileMutation(data);

            // Analytics
            stanAnalytics('profile-update', {
                meta: {
                    user_id: user?.user_id,
                    username: user?.username,
                },
                props: {
                    updates: data,
                    current: {
                        full_name: user?.full_name,
                        username: user?.username,
                        email: user?.email,
                        phone_number: user?.phone_number,
                    },
                },
            });

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Your profile has been updated successfully.',
            });
        } catch (error: any) {
            console.error('Profile update error:', error);

            if (error.response?.data?.errors) {
                setFormErrors(error.response.data.errors);
            } else {
                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: error?.response?.data?.message || 'Please try again later.',
                });
            }
        }
    };

    const onSubmit = async (data: ProfileFormData) => {
        // If username has changed, show confirmation modal
        if (hasUsernameChanged(data.username)) {
            pendingFormData.current = data;
            usernameModal.open();
            return;
        }

        // Otherwise, proceed with update directly
        await performUpdate(data);
    };

    const handleUsernameChangeConfirm = async () => {
        if (pendingFormData.current) {
            await performUpdate(pendingFormData.current);
            pendingFormData.current = null;
            usernameModal.close();
        }
    };

    const handleUsernameModalClose = () => {
        pendingFormData.current = null;
        usernameModal.close();
    };

    return (
        <section className='w-full max-w-3xl space-y-6'>
            <h4>My Profile</h4>

            <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <div>
                        <StanInput
                            {...register('full_name')}
                            name='full_name'
                            value={watchedValues.full_name || ''}
                            topLabel='Name'
                            error={!!errors.full_name || !!formErrors.full_name}
                            hintText={errors.full_name?.message || formErrors.full_name?.[0]}
                        />
                    </div>

                    {!isAffiliateOnlySubscription && (
                        <div>
                            <StanInput
                                {...register('username')}
                                name='username'
                                value={watchedValues.username || ''}
                                topLabel='Username'
                                error={!!errors.username || !!formErrors.username}
                                hintText={errors.username?.message || formErrors.username?.[0]}
                            />
                        </div>
                    )}
                </div>

                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <div>
                        <StanInput
                            {...register('email')}
                            name='email'
                            value={watchedValues.email || ''}
                            type='email'
                            topLabel='Email'
                            error={!!errors.email || !!formErrors.email}
                            hintText={errors.email?.message || formErrors.email?.[0]}
                        />
                    </div>

                    <div>
                        <Controller
                            name='phone_number'
                            control={control}
                            render={({ field }) => (
                                <StanPhoneInput
                                    id='profile-phone'
                                    name='phone_number'
                                    value={field.value || ''}
                                    onChange={(phoneChange: PhoneChange) => {
                                        field.onChange(phoneChange.e164 || phoneChange.national || '');
                                    }}
                                    label='Phone Number'
                                    error={!!errors.phone_number || !!formErrors.phone_number}
                                    hintText={errors.phone_number?.message || formErrors.phone_number?.[0]}
                                />
                            )}
                        />
                    </div>
                </div>

                {user?.referrer_user_id && (
                    <div className='grid grid-cols-1 gap-6'>
                        <div>
                            <StanInput
                                name='referrer_user'
                                value={user?.referrer_user_id?.toString() || ''}
                                topLabel='Referrer User'
                                disabled
                                onChange={() => {}}
                            />
                        </div>
                    </div>
                )}

                <div className='flex items-center gap-4'>
                    <StanButton
                        name='profile-update-button'
                        type='submit'
                        disabled={isButtonDisabled}
                        loading={isSubmitting}
                    >
                        Update
                    </StanButton>

                    {isSubmitting && (
                        <div className='text-caption-1-regular flex items-center'>
                            <div className='mr-2 size-4 animate-spin rounded-full border-b-2' />
                            Saving...
                        </div>
                    )}
                </div>
            </form>

            {/* Username Change Confirmation Modal */}
            <UsernameChangeModal
                isOpen={usernameModal.isOpen}
                newUsername={pendingFormData.current?.username || watchedValues.username || ''}
                isLoading={isSubmitting}
                onClose={handleUsernameModalClose}
                onConfirm={handleUsernameChangeConfirm}
            />
        </section>
    );
};
